/*
 * Widget registry. Keeps widget type definitions out of the layout host so
 * hosts stay engine-only and adding a new widget doesn't require touching the
 * grid code. Step 8 replaces these placeholders with real telemetry widgets;
 * the registry API stays the same.
 *
 * Extension surface: a plugin calls `registerWidget({ id, displayName, render })`
 * to inject a new type — the DashboardPage then renders it wherever an
 * instance references its `id`.
 */

import type { VNode } from 'preact';
import type { WidgetInstance } from './layoutHost';
import type { PlantSchemaResponse } from '../api/types';

/**
 * Ambient context handed to every widget's render function. Carries enough
 * for a data-bound widget to resolve a leafId to metadata (unit, decimals,
 * thresholds) and to subscribe to the live stream. Presentational widgets
 * are free to ignore it entirely.
 */
export interface WidgetRenderContext {
  /** The plant this dashboard belongs to. */
  plantId: string;
  /** The plant schema (device/node/leaf tree). May be null while loading —
   *  data-bound widgets should render a "waiting for schema" state. */
  plantSchema: PlantSchemaResponse | null;
}

export interface WidgetType {
  /** Registry key. Convention: `<namespace>.<name>` (e.g. `placeholder.value`). */
  id: string;
  /** Human-readable label — shown in the widget-picker (future) and the card title. */
  displayName: string;
  /** Short description for the widget picker. */
  description: string;
  /** Optional "bare" chrome: strips the grid cell's border + padding +
   *  background so the widget content sits directly on the dashboard
   *  canvas. Used for text/heading/separator/spacer — they should read
   *  as raw page content, not as boxed cards. Data-bound widgets stay
   *  card-styled by default. When absent, treated as `'card'`. Can be a
   *  function so a single widget type (e.g. image) can decide per-instance
   *  based on its config. */
  chrome?: 'card' | 'bare' | ((instance: WidgetInstance) => 'card' | 'bare');
  /** Render an instance. Kept as a plain function so the registry doesn't
   *  force JSX. Receives ambient context (plantId + plantSchema) so
   *  data-bound widgets can look up leaf metadata + subscribe to signals. */
  render(instance: WidgetInstance, ctx: WidgetRenderContext): VNode;
}

const registry = new Map<string, WidgetType>();

/**
 * Register a widget type. Idempotent — re-registering with the same id
 * replaces the previous entry (useful for HMR / plugin reloads).
 */
export function registerWidget(type: WidgetType): void {
  registry.set(type.id, type);
}

/** Lookup a widget type by id. Returns undefined for unknown ids so the host
 *  can decide how to render a "missing widget" placeholder. */
export function getWidget(id: string): WidgetType | undefined {
  return registry.get(id);
}

/** Enumerate every registered widget type. Used by the widget-picker (Step 8). */
export function listWidgets(): WidgetType[] {
  return Array.from(registry.values());
}

// Real widget registrations land in a subsequent step. The registry API is
// intentionally left populated with nothing so a fresh dashboard starts empty
// instead of showing placeholder content — the WidgetPicker is the entry
// point for adding widgets. DashboardPage renders an explicit fallback for
// any widgetTypeId that isn't in the registry, so unknown ids remain
// diagnosable rather than silently missing.
